import {
  Button,
  CircularProgress,
  FormControlLabel,
  RadioGroup,
  Typography,
} from '@mui/material';
import type {
  PermissionAction,
  PermissionGroupId,
  PermissionGroupValues,
  ProfilePermissions,
  zPermissionScope,
} from '@strength/domain';
import { groupActionRows, permissionDependencies } from '@strength/domain';
import { MdExpandMore } from '@teamworksdev/icons';
import { useEffect, useState } from 'react';
import type { Control, FieldPath } from 'react-hook-form';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { trpc } from '../../../trpc/react';import { Drawer, DrawerFooter, DrawerHeader } from '../../components/drawer';
import { LabeledRadioChip } from '../../components/input/labeled-radio-chip';
import { Checkbox } from '../../components/input/Checkbox';
import { ConfirmationModal } from '../../components/modals/confirmation-modal';
import { cn } from '../../components/utils';
import { useToast } from '../../hooks/useToast';
import { resolvePermissionsDocument } from './permission-drawer-state';
import { CATEGORY_ORDER, GROUPS_BY_CATEGORY } from './permission-group-config';

type PermissionScope = z.infer<typeof zPermissionScope>;

/**
 * The group/category/action label maps are built from `@strength/domain`
 * data, so their translation keys can't be statically narrowed to i18next's
 * generated key union the way literal `t('someKey')` calls are. Routed
 * through one cast (as `unknown`, not `any`, to keep the lint rule quiet)
 * rather than scattering casts at every call site.
 */
function translateKey(
  t: ReturnType<typeof useTranslation>['t'],
  key: string,
  interpolation?: Record<string, string>,
): string {
  const translate = t as unknown as (
    key: string,
    interpolation?: Record<string, string>,
  ) => string;
  return translate(key, interpolation);
}

/**
 * Shared card chrome for the drawer's sections. `fieldset` keeps the grouping
 * semantics, and the UA's default fieldset margin/min-width are reset so the
 * cards line up flush inside the drawer's flex column.
 */
const CARD_CLASS =
  'mx-0 mt-0 mb-6 min-w-0 rounded-md border border-gray-200 bg-white p-6';

const SCOPE_OPTIONS = [
  { value: 'org', labelKey: 'permissionsScopeOrg' },
  { value: 'team', labelKey: 'permissionsScopeTeam' },
] as const;

/**
 * The drawer's form state. `permissions` is a fully-resolved document (every
 * action an explicit boolean), so React Hook Form's `isDirty` — a comparison
 * against the `reset` defaults — is exactly "differs from what's stored", and
 * the value can be submitted as-is.
 */
type PermissionFormValues = {
  scope: PermissionScope;
  permissions: ProfilePermissions;
};

/**
 * Group ids are dot paths into the document (`library.tags`), which is also
 * React Hook Form's nested-field syntax — so a group's field path is just its
 * id under `permissions`. The cast is needed because `PermissionAction` is the
 * union across every group, so the generated path union rejects combinations
 * no group actually has (only `plan.tracks` has `archive`).
 */
function fieldPath(
  groupId: PermissionGroupId,
  action?: PermissionAction,
): FieldPath<PermissionFormValues> {
  const path = action
    ? `permissions.${groupId}.${action}`
    : `permissions.${groupId}`;
  return path as FieldPath<PermissionFormValues>;
}

export type UserPermissionsDrawerProps = {
  profileId: string | null;
  coachName?: string;
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
};

function PermissionGroupCard({
  groupId,
  titleKey,
  control,
  expanded,
  onToggleExpand,
}: {
  groupId: PermissionGroupId;
  titleKey: string;
  control: Control<PermissionFormValues>;
  expanded: boolean;
  onToggleExpand: () => void;
}) {
  const { t } = useTranslation();
  const title = translateKey(t, titleKey);
  const contentId = `permission-group-${groupId}-content`;
  const rows = groupActionRows(groupId);
  // Watching just this group keeps the enabled count live while a collapsed
  // card re-renders no more often than its own checkboxes change.
  const values = (useWatch({ control, name: fieldPath(groupId) }) ??
    {}) as PermissionGroupValues;
  // What the group's dependencies resolve to given the current field values —
  // sub-actions render denied/locked while their gate is off, and the enabled
  // count only credits actions that actually grant access.
  const dependencyDeniedBySubAction = new Map(
    permissionDependencies(groupId).map(({ subAction, dependsOn }) => [
      subAction,
      values[dependsOn as PermissionAction] !== true,
    ]),
  );
  const enabled = rows.filter(
    ({ action }) =>
      values[action] === true && !dependencyDeniedBySubAction.get(action),
  ).length;

  return (
    <fieldset className={CARD_CLASS}>
      {/*
       * The visible title lives in the disclosure button, so the legend is
       * screen-reader-only: a rendered legend punches a gap in the fieldset's
       * top border, which the design's cards don't have.
       */}
      <legend className="sr-only">{title}</legend>
      <button
        type="button"
        onClick={onToggleExpand}
        aria-expanded={expanded}
        aria-controls={contentId}
        className="flex w-full flex-col text-left bg-white p-0"
      >
        {/* The chevron centers on the title row; the count sits below both. */}
        <span className="flex w-full flex-row items-center justify-between gap-2">
          <Typography
            variant="body1"
            component="span"
            className="font-semibold"
          >
            {title}
          </Typography>
          <MdExpandMore
            size={24}
            className={cn(
              'shrink-0 transition-transform',
              expanded && 'rotate-180',
            )}
            aria-hidden
          />
        </span>
        <Typography variant="body2" color="text.secondary" component="span">
          {t('permissionsActionsEnabled', { enabled, total: rows.length })}
        </Typography>
      </button>
      {expanded && (
        <div id={contentId} className="mt-4 flex flex-col gap-4">
          {rows.map(({ action, descriptionKey, depth }) => {
            const gated = dependencyDeniedBySubAction.get(action) === true;
            return (
              <Controller
                key={action}
                control={control}
                name={fieldPath(groupId, action)}
                render={({ field }) => {
                  const gatedValue = gated ? false : field.value;
                  return (
                    <FormControlLabel
                      // Root actions sit at the card's left edge; children are
                      // indented one level per depth, so they read as building
                      // on their parent. Top-aligned so the checkbox lines up
                      // with the title rather than centering against the
                      // description.
                      className={cn(
                        'm-0 items-start gap-2',
                        depth > 0 && 'ml-6',
                      )}
                      control={
                        <Checkbox
                          checked={gatedValue === true}
                          disabled={gated}
                          onBlur={field.onBlur}
                          onChange={field.onChange}
                        />
                      }
                      label={
                        <span className="flex flex-col">
                          <Typography variant="body2" className="font-medium">
                            {translateKey(t, action)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {translateKey(t, descriptionKey, {
                              resource: title.toLowerCase(),
                            })}
                          </Typography>
                        </span>
                      }
                    />
                  );
                }}
              />
            );
          })}
        </div>
      )}
    </fieldset>
  );
}

export function UserPermissionsDrawer({
  profileId,
  coachName,
  open,
  onClose,
  onSaved,
}: UserPermissionsDrawerProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [collapsedGroups, setCollapsedGroups] = useState<
    Set<PermissionGroupId>
  >(new Set());
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  // The stored document is the form's defaults, so it has to be `reset` in
  // once loaded; this tracks whose document the current defaults came from.
  const [loadedProfileId, setLoadedProfileId] = useState<string | null>(null);

  const { control, handleSubmit, reset, formState } =
    useForm<PermissionFormValues>({
      defaultValues: {
        scope: 'org',
        permissions: resolvePermissionsDocument({}),
      },
    });
  const { isDirty } = formState;

  const permissionsQuery = trpc.permissions.permissionsForProfile.useQuery(
    { profileId: profileId ?? '' },
    { enabled: open && !!profileId },
  );
  const { refetch: refetchPermissions } = permissionsQuery;

  // Fresh-per-open: every open refetches the stored document and the form
  // only ever seeds from the settled refetch result — never from whatever
  // the cache hands back while the fetch is in flight (a saved change used
  // to read back as the pre-save cache on the next open). Selecting a
  // different coach while open refetches and re-seeds the same way.
  useEffect(() => {
    if (!open) {
      setLoadedProfileId(null);
      setCollapsedGroups(new Set());
      return;
    }
    if (!profileId) return;
    let cancelled = false;
    refetchPermissions().then((result) => {
      if (cancelled || result.isError || !result.data) return;
      reset({
        scope: result.data.scope,
        permissions: resolvePermissionsDocument(result.data.permissions),
      });
      setLoadedProfileId(profileId);
    });
    return () => {
      cancelled = true;
    };
  }, [open, profileId, refetchPermissions, reset]);

  const loaded = !!profileId && loadedProfileId === profileId;

  const trpcUtils = trpc.useUtils();
  const savePermissionsMutation = trpc.permissions.savePermissions.useMutation({
    onSuccess: () => {
      toast({ title: t('permissionsSaved'), severity: 'success' });
      // The saved document takes effect immediately for the viewer too (the
      // server already invalidated its role cache) — refetch the viewer's own
      // resolved permissions so hasPermissions consumers re-render without a
      // full page refresh, and mark the profile's stored document stale so
      // reopening the drawer refetches it instead of reading the pre-save
      // cache back into the form. No-op cost when editing someone else.
      trpcUtils.permissions.myPermissions.invalidate();
      trpcUtils.permissions.permissionsForProfile.invalidate({
        profileId: profileId ?? '',
      });
      onSaved?.();
      onClose();
    },
    onError: () => {
      toast({ title: t('permissionsSaveError'), severity: 'error' });
    },
  });

  const handleToggleExpand = (groupId: PermissionGroupId) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const handleCancelClick = () => {
    if (isDirty) {
      setDiscardConfirmOpen(true);
    } else {
      onClose();
    }
  };

  const handleSave = handleSubmit((values) => {
    if (!profileId) return;
    savePermissionsMutation.mutate({
      profileId,
      scope: values.scope,
      permissions: values.permissions,
    });
  });

  return (
    <Drawer open={open} onClose={handleCancelClick}>
      <DrawerHeader onClose={handleCancelClick}>
        <Typography variant="h3">{t('userPermissions')}</Typography>
        {coachName && (
          <Typography variant="body2" color="text.secondary">
            {coachName}
          </Typography>
        )}
      </DrawerHeader>

      <div className="flex grow flex-col overflow-y-auto p-6">
        {!loaded && !permissionsQuery.isError && (
          <div className="flex justify-center p-6">
            <CircularProgress aria-label={t('loading')} />
          </div>
        )}

        {permissionsQuery.isError && (
          <Typography color="error" className="p-6">
            {t('errorLoadingData')}
          </Typography>
        )}

        {loaded && (
          <>
            <fieldset className={CARD_CLASS}>
              <legend className="sr-only">{t('permissionsScope')}</legend>
              <Typography
                variant="body1"
                component="p"
                className="font-semibold"
              >
                {t('permissionsScope')}
              </Typography>
              <Controller
                control={control}
                name="scope"
                render={({ field }) => (
                  <RadioGroup {...field} row sx={{ gap: 1, marginTop: 2 }}>
                    {SCOPE_OPTIONS.map((option) => (
                      <LabeledRadioChip
                        key={option.value}
                        value={option.value}
                        label={t(option.labelKey)}
                      />
                    ))}
                  </RadioGroup>
                )}
              />
            </fieldset>

            {CATEGORY_ORDER.map((category) => {
              const groups = GROUPS_BY_CATEGORY[category];
              return (
                <div key={category}>
                  {groups.map((group) => (
                    <PermissionGroupCard
                      key={group.id}
                      groupId={group.id}
                      titleKey={group.titleKey}
                      control={control}
                      expanded={!collapsedGroups.has(group.id)}
                      onToggleExpand={() => handleToggleExpand(group.id)}
                    />
                  ))}
                </div>
              );
            })}
          </>
        )}
      </div>

      <DrawerFooter className="flex flex-row gap-2 w-full p-6 justify-end">
        <ConfirmationModal
          id="user-permissions-discard"
          open={discardConfirmOpen}
          title={t('discardPermissionsChangesTitle')}
          message={t('discardPermissionsChangesMessage')}
          onConfirm={() => {
            setDiscardConfirmOpen(false);
            onClose();
          }}
          onCancel={() => setDiscardConfirmOpen(false)}
        />
        <Button variant="outlined" onClick={handleCancelClick}>
          {t('cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!isDirty || savePermissionsMutation.isLoading}
        >
          {t('save')}
        </Button>
      </DrawerFooter>
    </Drawer>
  );
}
